/**
 * InspireRoomCard
 * Masonry-style card for one photo of a public Finishd project. Every field
 * comes from the public-projects API; the card opens the project it shows.
 */

import { Heart } from "lucide-react";
import { useTranslation } from "react-i18next";

export interface InspireRoom {
	/** `${projectId}:${imageIndex}` — one card per preview image. */
	id: string;
	projectId: string;
	title: string;
	city: string | null;
	designer: string | null;
	rooms: string[];
	image: string;
	/** Tailwind aspect class cycled by the page for the masonry layout. */
	aspectRatio: string;
}

interface InspireRoomCardProps {
	room: InspireRoom;
	onOpen?: (projectId: string) => void;
	/** Whether the project this photo belongs to is saved. */
	saved?: boolean;
	/** A save or unsave of this project is in flight. */
	savePending?: boolean;
	/** Omit to leave the save control off the card. */
	onToggleSave?: (projectId: string) => void;
}

function RoomCardButton({
	room,
	onOpen,
}: Pick<InspireRoomCardProps, "room" | "onOpen">) {
	// A project may declare the same room name twice; the pills (and their
	// React keys) want each name once.
	const uniqueRooms = [...new Set(room.rooms)];

	return (
		<button
			type="button"
			className="relative block w-full rounded-lg cursor-pointer group text-left border-0 bg-transparent p-0"
			onClick={() => onOpen?.(room.projectId)}
		>
			{/* ── Image card (overflow-hidden clips image + overlays only) ── */}
			<div
				className={`relative w-full rounded-lg overflow-hidden shadow-[0_2px_4px_color-mix(in_srgb,var(--color-dashboard-shadow-ink)_4%,transparent),0_8px_20px_-6px_color-mix(in_srgb,var(--color-dashboard-shadow-ink)_14%,transparent)] transition-shadow duration-380 group-hover:shadow-[0_12px_28px_color-mix(in_srgb,var(--color-dashboard-shadow-ink)_22%,transparent)] ${room.aspectRatio}`}
			>
				<img
					src={room.image}
					alt={room.title}
					className="absolute inset-0 w-full h-full object-cover saturate-[0.94] contrast-[1.06] transition-transform duration-1400 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.05]"
				/>

				{/* Default gradient — fades out on hover */}
				<div className="absolute inset-0 bg-linear-to-b from-dashboard-scrim/10 via-transparent to-dashboard-scrim/55 opacity-100 group-hover:opacity-0 transition-opacity duration-380" />

				{/* Dark hover overlay — fades in on hover */}
				<div className="absolute inset-0 bg-linear-to-b from-dashboard-scrim/42 via-transparent to-dashboard-scrim/92 opacity-0 group-hover:opacity-100 transition-opacity duration-380" />

				{/* Warm radial glow — fades in on hover */}
				<div className="absolute inset-0 bg-[radial-gradient(at_80%_100%,var(--color-dashboard-accent)/20_0%,transparent_55%)] opacity-0 group-hover:opacity-100 transition-opacity duration-380" />

				{/* ── Simple title — fades out on hover ── */}
				<div className="absolute left-4 right-3.5 bottom-3 text-dashboard-on-fill opacity-100 translate-y-0 group-hover:opacity-0 group-hover:translate-y-1.5 transition-all duration-320 ease-out">
					<div className="font-display italic font-medium text-lg tracking-[-0.005em]">
						{room.title}
					</div>
				</div>
			</div>
			{/* ── End image card ── */}

			{/*
			 * Detail hover panel — lives OUTSIDE the overflow-hidden image div so
			 * it can grow upward without being clipped on short-aspect cards.
			 */}
			<div className="absolute flex flex-col gap-3 left-3 right-3 bottom-3 p-4 rounded-[7px] border border-dashboard-on-photo-warm/18 text-dashboard-on-fill opacity-0 translate-y-3 pointer-events-none group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-380 ease-[cubic-bezier(0.22,1,0.36,1)] bg-linear-to-b from-dashboard-scrim/65 to-dashboard-scrim/85 backdrop-blur-[20px] backdrop-saturate-[1.3] shadow-[0_8px_24px_-4px_rgba(0,0,0,0.45)]">
				{room.city && (
					<div className="font-mono text-[8.5px] font-semibold tracking-[0.2em] text-dashboard-on-photo-warm/78 uppercase">
						{room.city}
					</div>
				)}

				<div className="font-display font-medium text-[22px] tracking-[-0.015em] leading-[1.1]">
					{room.title}
					{room.designer && (
						<span
							className="italic text-dashboard-on-photo-warm"
							style={{ letterSpacing: "-0.005em" }}
						>
							{" "}
							by {room.designer}.
						</span>
					)}
				</div>

				{uniqueRooms.length > 0 && (
					<div className="flex flex-wrap gap-1 mt-1">
						{uniqueRooms.map((name) => (
							<span
								key={name}
								className="px-2 py-0.75 rounded-full bg-dashboard-on-photo-warm/14 font-mono text-[8.5px] font-semibold tracking-[0.16em] text-dashboard-on-photo-warm uppercase"
							>
								{name}
							</span>
						))}
					</div>
				)}

				<span className="font-body italic text-xs text-dashboard-on-fill/85">
					Open this project
				</span>
			</div>
		</button>
	);
}

/**
 * One photo of a public project. Saving is per project, so every photo of a
 * project shows the same saved state.
 */
export function InspireRoomCard({
	room,
	onOpen,
	saved = false,
	savePending = false,
	onToggleSave,
}: InspireRoomCardProps) {
	const { t } = useTranslation();

	return (
		<div className="relative">
			<RoomCardButton room={room} onOpen={onOpen} />
			{onToggleSave && (
				<button
					type="button"
					aria-pressed={saved}
					aria-label={t("inspire.saveIdea", { title: room.title })}
					disabled={savePending}
					onClick={() => onToggleSave(room.projectId)}
					className="absolute top-3 right-3 z-10 flex h-9 w-9 items-center justify-center rounded-full border border-dashboard-on-fill/40 bg-dashboard-ink/45 text-dashboard-on-fill backdrop-blur-sm transition hover:bg-dashboard-ink/65 disabled:cursor-wait disabled:opacity-60"
				>
					<Heart
						className="h-4 w-4"
						fill={saved ? "currentColor" : "none"}
						aria-hidden="true"
					/>
				</button>
			)}
		</div>
	);
}
